import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  VehicleServiceCouponCheckResult,
  VehicleServiceQuickSearchForm,
  VehicleServiceQuickSearchResult,
  VehicleServiceRecentSearchesResult,
  VehicleServiceSearchResultsQueryPayload,
  VehicleServiceSearchResultsResult,
  VehicleServiceServicesResult,
  VehicleServiceWorkshopInfoResult
} from '~/models/vehicle-service'
import { toSnakeCase } from '~/utils/snake-case'

@containerScoped()
export default class VehicleServiceService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  getQuickSearchForm(
    form: VehicleServiceQuickSearchForm = {}
  ): Promise<VehicleServiceQuickSearchResult> {
    return this.requestBuilder
      .request('get', '/api/search-service/makemodel/')
      .params(toSnakeCase(form))
      .send()
  }

  addSearchToRecentSearches(data: {
    make: string
    model: string
    generation: string
    modelCategory: string
    carId: string
  }): Promise<VehicleServiceRecentSearchesResult> {
    return this.requestBuilder
      .request('post', '/api/search-service/recent-searches/')
      .params(toSnakeCase(data))
      .send()
  }

  getRecentSearches(): Promise<VehicleServiceRecentSearchesResult> {
    return this.requestBuilder
      .request('get', '/api/search-service/recent-searches/')
      .send()
  }

  getServices({
    carId,
    token
  }: {
    carId: string
    token?: string
  }): Promise<VehicleServiceServicesResult> {
    return this.requestBuilder
      .request('get', '/api/search-service/services/')
      .params({ car_id: carId, token })
      .send()
  }

  getSearchResults(
    queryPayload: VehicleServiceSearchResultsQueryPayload
  ): Promise<VehicleServiceSearchResultsResult> {
    return this.requestBuilder
      .request('get', '/api/search-service/workshops/')
      .params({
        ...toSnakeCase(queryPayload),
        drive_type_4x4: queryPayload.driveType4x4,
        coupon_name: queryPayload.couponName
          ? queryPayload.couponName
          : undefined
      })
      .send()
  }

  getWorkshopInfo({
    workshopId,
    token
  }: {
    workshopId: string
    token: string
  }): Promise<VehicleServiceWorkshopInfoResult> {
    return this.requestBuilder
      .request('get', `/api/search-service/workshops/${workshopId}/`)
      .params({ token })
      .send()
  }

  getWorkshopAvailability({
    workshopId,
    token
  }: {
    workshopId: string
    token: string
  }): Promise<{ availability: string[] }> {
    return this.requestBuilder
      .request(
        'get',
        `/api/search-service/workshops/${workshopId}/availability/`
      )
      .params({ token })
      .send()
  }

  getMobileValidationPin({
    mobilePhone,
    token
  }: {
    mobilePhone: string
    token: string
  }): Promise<any> {
    return this.requestBuilder
      .request('get', '/api/search-service/check-phone/')
      .params({ mobile_phone: mobilePhone, token })
      .send()
  }

  validateMobilePin({
    userPin,
    token,
    mobilePhone,
    systemPin,
    userId
  }: {
    userPin: number
    token: string
    mobilePhone: string
    systemPin: number
    userId?: number
  }): Promise<{ gdprAgreements: boolean; validated: boolean }> {
    return this.requestBuilder
      .request('post', '/api/search-service/check-phone/')
      .data({
        user_pin: userPin,
        token,
        mobile_phone: {
          telephone: mobilePhone,
          code: 30
        },
        system_pin: systemPin,
        user_id: userId
      })
      .send()
  }

  checkCoupon(coupon: string): Promise<VehicleServiceCouponCheckResult> {
    return this.requestBuilder
      .request('post', '/api/search-service/check-coupon/')
      .data({ coupon })
      .send()
  }

  createOrderAsNewUser({
    appointmentDate,
    firstName,
    lastName,
    workshopId,
    token,
    mobilePhone,
    email
  }: {
    appointmentDate: string
    firstName: string
    lastName: string
    workshopId: string
    token: string
    mobilePhone: string
    email: string
  }): Promise<{ created: boolean; orderId: number }> {
    return this.requestBuilder
      .request('post', '/api/search-service/create-order-new-user/')
      .data({
        available: appointmentDate,
        first_name: firstName,
        last_name: lastName,
        workshop_select: workshopId,
        token,
        mobile_phone: mobilePhone,
        email
      })
      .send()
  }

  createOrderAsExistingUser({
    appointmentDate,
    workshopId,
    token,
    userId
  }: {
    appointmentDate: string
    workshopId: string
    token: string
    userId: number
  }): Promise<{ created: boolean; orderId: number }> {
    return this.requestBuilder
      .request('post', '/api/search-service/create-order/')
      .data({
        available: appointmentDate,
        workshop_select: workshopId,
        token,
        user_id: userId
      })
      .send()
  }

  storePremiumPartsInfo({
    token,
    hasPremiumParts
  }: {
    token: string
    hasPremiumParts: boolean
  }): Promise<void> {
    return this.requestBuilder
      .request('post', '/api/search-service/premium-parts/')
      .data({ token, enabled_premium: hasPremiumParts })
      .send()
  }
}
